import { Extension } from "@tiptap/core";
import { TextSelection, EditorState, Transaction } from "@tiptap/pm/state";

function isWrapped(text: string, wrapper: string) {
  return text.startsWith(wrapper) && text.endsWith(wrapper) && text.length > 2 * wrapper.length;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    markdownFormatting: {
      toggleMarkdownBold: () => ReturnType;
      toggleMarkdownItalic: () => ReturnType;
    };
  }
}

export const MarkdownFormatting = Extension.create({
  name: "markdownFormatting",

  addKeyboardShortcuts() {
    return {
      "Mod-b": () => this.editor.commands.toggleMarkdownBold(),
      "Mod-i": () => this.editor.commands.toggleMarkdownItalic(),
    };
  },

  addCommands() {
    return {
      toggleMarkdownBold:
        () =>
        ({
          state,
          tr,
          dispatch,
        }: {
          state: EditorState;
          tr: Transaction;
          dispatch?: (tr: Transaction) => void;
        }) => {
          const { from, to, empty } = state.selection;

          if (empty) {
            // Check if we're sitting inside a pair of **
            const beforeStart = Math.max(0, from - 2);
            const afterEnd = Math.min(state.doc.content.size, from + 2);
            const before = beforeStart < from ? state.doc.textBetween(beforeStart, from) : "";
            const after = afterEnd > from ? state.doc.textBetween(from, afterEnd) : "";

            if (before === "**" && after === "**") {
              // Remove the unfilled bold markers
              tr.delete(beforeStart, afterEnd);
              tr.setSelection(TextSelection.create(tr.doc, beforeStart));
              if (dispatch) {
                dispatch(tr);
              }
              return true;
            }
            // Insert ** and position cursor in middle
            tr.insertText("****", from);
            tr.setSelection(TextSelection.create(tr.doc, from + 2));
            if (dispatch) {
              dispatch(tr);
            }
            return true;
          }
          const selectedText = state.doc.textBetween(from, to);

          // Check if the selection is already wrapped with ** (including surrounding text)
          const expandedFrom = Math.max(0, from - 2);
          const expandedTo = Math.min(state.doc.content.size, to + 2);

          // Check if we have ** before and after the selection
          const hasBoldBefore =
            expandedFrom < from && state.doc.textBetween(expandedFrom, from) === "**";
          const hasBoldAfter = expandedTo > to && state.doc.textBetween(to, expandedTo) === "**";

          if (hasBoldBefore && hasBoldAfter) {
            // Remove the ** markers around the selection
            tr.delete(to, expandedTo); // Remove after first (positions shift after deletion)
            tr.delete(expandedFrom, from); // Remove before
            tr.setSelection(
              TextSelection.create(tr.doc, expandedFrom, expandedFrom + selectedText.length),
            );
            if (dispatch) {
              dispatch(tr);
            }
            return true;
          } else if (isWrapped(selectedText, "**")) {
            // Selection itself contains the markers
            const unwrapped = selectedText.slice(2, -2);
            tr.replaceWith(from, to, state.schema.text(unwrapped));
            tr.setSelection(TextSelection.create(tr.doc, from, from + unwrapped.length));
            if (dispatch) {
              dispatch(tr);
            }
            return true;
          }
          // Wrap selected text in **
          tr.replaceWith(from, to, state.schema.text(`**${selectedText}**`));
          // Select just the text inside the markers
          tr.setSelection(TextSelection.create(tr.doc, from + 2, from + 2 + selectedText.length));
          if (dispatch) {
            dispatch(tr);
          }
          return true;
        },

      toggleMarkdownItalic:
        () =>
        ({
          state,
          tr,
          dispatch,
        }: {
          state: EditorState;
          tr: Transaction;
          dispatch?: (tr: Transaction) => void;
        }) => {
          const { from, to, empty } = state.selection;

          if (empty) {
            // Check if we're sitting inside a pair of *
            const beforeStart = Math.max(0, from - 1);
            const afterEnd = Math.min(state.doc.content.size, from + 1);
            const before = beforeStart < from ? state.doc.textBetween(beforeStart, from) : "";
            const after = afterEnd > from ? state.doc.textBetween(from, afterEnd) : "";

            if (before === "*" && after === "*") {
              // Remove the unfilled italic markers
              tr.delete(beforeStart, afterEnd);
              tr.setSelection(TextSelection.create(tr.doc, beforeStart));
              if (dispatch) {
                dispatch(tr);
              }
              return true;
            }
            // Insert ** and position cursor in middle
            tr.insertText("**", from);
            tr.setSelection(TextSelection.create(tr.doc, from + 1));
            if (dispatch) {
              dispatch(tr);
            }
            return true;
          }
          const selectedText = state.doc.textBetween(from, to);

          // Check if the selection is already wrapped with * (including surrounding text)
          const expandedFrom = Math.max(0, from - 1);
          const expandedTo = Math.min(state.doc.content.size, to + 1);

          // Check if we have * before and after the selection
          const hasItalicBefore =
            expandedFrom < from && state.doc.textBetween(expandedFrom, from) === "*";
          const hasItalicAfter = expandedTo > to && state.doc.textBetween(to, expandedTo) === "*";

          if (hasItalicBefore && hasItalicAfter) {
            // Remove the * markers around the selection
            tr.delete(to, expandedTo); // Remove after first (positions shift after deletion)
            tr.delete(expandedFrom, from); // Remove before
            tr.setSelection(
              TextSelection.create(tr.doc, expandedFrom, expandedFrom + selectedText.length),
            );
            if (dispatch) {
              dispatch(tr);
            }
            return true;
          } else if (isWrapped(selectedText, "*")) {
            // Selection itself contains the markers
            const unwrapped = selectedText.slice(1, -1);
            tr.replaceWith(from, to, state.schema.text(unwrapped));
            tr.setSelection(TextSelection.create(tr.doc, from, from + unwrapped.length));
            if (dispatch) {
              dispatch(tr);
            }
            return true;
          }
          // Wrap selected text in *
          tr.replaceWith(from, to, state.schema.text(`*${selectedText}*`));
          // Select just the text inside the markers
          tr.setSelection(TextSelection.create(tr.doc, from + 1, from + 1 + selectedText.length));
          if (dispatch) {
            dispatch(tr);
          }
          return true;
        },
    };
  },
});
